import { LocalStorageHandler, TemporaryStorageService } from '@imaios-js/utils';
import NOTIFICATIONS from '../../../assets/js/notifications.constant';
import { COOKIE, PRODUCT, STORAGE_KEY } from '../../../assets/js/imaios-connection-management/config.constant';
import { fetch, getCookie } from '@assets/js/imaios-global/imaios-global';

const E_ANATOMY = 'e-anatomy';
const VET_ANATOMY = 'vet-anatomy';

const SIQ = {
  ACCESS: {
    GRANTED: 'granted',
    NO_LICENSE: 'no_license'
  },
  DEFAULT_USER_ID: 10
};

const MAPPING_PRODUCT = [
  { subscription: PRODUCT.E_ANATOMY, database: E_ANATOMY },
  { subscription: PRODUCT.VET_ANATOMY, database: VET_ANATOMY }
];

const MAP_PRODUCT_SHORTCODE_DATABASE = {
  'EAN': E_ANATOMY,
  'VAN': VET_ANATOMY
};

export const COUNTER_PARAMETERS = {
  event_type: 'event_type',
  platform: 'platform',
  database: 'database',
  doi: 'doi',
  url: 'url',
  session_id: 'session_id',
  user_id: 'user_id',
  user_access: 'user_access',
  device: 'device',
  access: 'access',
  account_id: 'account_id',
  site_name: 'sitename',
  context: 'context'
};

export const CONTEXT = 'china';

/**
 * @typedef {Object} SiqInformations
 * @property {string} session_id
 * @property {string} siq_id
 * @property {string} siq_licence
 * @property {string} siq_name
 * @property {boolean} siq_send_log
 * @property {boolean} siq_b2b_is_ip
 * @property {string} siq_as
 * @property {string} siq_asn
 */

/**
 * @typedef {Object} ThirdPartyCookieData
 * @property {string} id
 * @property {string} scholarlyiq_id
 * @property {string} product
 * @property {string} cuid
 * @property {string} key
 * @property {?string} provider
 */

export default class ScholarlyIq {
  constructor() {
    // Remove all expires key
    TemporaryStorageService.checkAllExpire(new RegExp(`${STORAGE_KEY.SIQ_INFORMATION}-.*`));

    // Add this global variable for the application e/vet-anatomy for capping
    window.siq = ScholarlyIq;
  }

  static getLocalStorageKey(database, user_id) {
    return `${STORAGE_KEY.SIQ_INFORMATION}-${database}_${user_id}`;
  }

  static getDeviceId() {
    let deviceId = LocalStorageHandler.get('im_device_id');

    if (!deviceId) {
      deviceId = this.generateDeviceId();
      LocalStorageHandler.add('im_device_id', deviceId);
    }

    return deviceId;
  }

  static generateDeviceId() {
    return `w_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   *
   * @param {URLSearchParams} parameters
   * @param {string} hostImaiosEvent
   */
  static sendImaiosEvent(parameters, hostImaiosEvent) {
    parameters.set('cache_nonce', Date.now().toString());
    window.imaios.fetch(`https://${hostImaiosEvent}/ptg?${parameters.toString()}`, { method: 'GET' });
  }

  /**
   *
   * @param {string} event_type
   * @param {string} database
   * @param {string} doi
   * @param {string} yop
   * @param {LocalUserData} information
   * @return {Promise<void>}
   */
  async getSiqInformation(event_type, database, doi, yop, information) {
    let user_id = SIQ.DEFAULT_USER_ID;
    if (information.id !== null) {
      user_id = information.id;
    }

    const localstorageKey = ScholarlyIq.getLocalStorageKey(database, user_id);
    TemporaryStorageService.checkExpire(localstorageKey);
    /** @type {SiqInformations|null} */
    let data = TemporaryStorageService.get(localstorageKey);

    // Clear data if old parameters exist
    if (typeof data?.im_institution_id !== 'undefined') {
      data = null;
    }

    // If we don't have the SIQ information in storage retrieve them and save them
    if (data === null) {
      // Create url to retrieve information for SIQ
      let url = `/${window.imaios.getSiteAccess()}/api/siq/information`;
      if (database.indexOf(',') === -1) {
        let params = MAPPING_PRODUCT.find(element => element.database === database);
        let query = Object.keys(params)
          .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
          .join('&');
        url += '?' + query;
      }

      // Retrieve SIQ information
      const res = await fetch(url);
      data = await res.json();
      if (typeof data === 'object' && Object.keys(data).length > 0) {
        // Save SIQ information
        TemporaryStorageService.add(localstorageKey, data);
      }
    }

    // Try to send SIQ information
    if (typeof data === 'object' && Object.keys(data).length > 0) {
      this.updateDataFromThirdPartyProviderCookie(data);
      // Send SIQ information
      try {
        window.imaios.pubsub.publish(NOTIFICATIONS.SIQ_INFORMATION_RESOLVED);
      }
      catch (error) {
        console.error(error);
      }
      this.sendToImaiosEvent(data, event_type, database, doi, yop, information);
    }
  }

  /**
   * @param {SiqInformations} data
   * @param {string} event_type
   * @param database
   * @param doi
   * @param yop
   * @param information
   */
  sendToImaiosEvent(data, event_type, database, doi, yop, information) {
    // Add user id if exist
    let userId = this.getUserId(data, information);

    const parameters = new URLSearchParams();
    parameters.set(COUNTER_PARAMETERS.user_id, userId);
    parameters.set(COUNTER_PARAMETERS.url, window.location.href);
    parameters.set(COUNTER_PARAMETERS.platform, 'web');
    parameters.set(COUNTER_PARAMETERS.database, database);
    parameters.set(COUNTER_PARAMETERS.event_type, event_type);
    parameters.set(COUNTER_PARAMETERS.context, CONTEXT);

    // Get product id
    const productIds = MAPPING_PRODUCT
      .filter(element => database.includes(element.database))
      .map(element => element.subscription);

    // Case when the user is in a group or an institution
    if (data.siq_id !== '') {
      let access;
      if (data.siq_b2b_is_ip) {
        access = this.getUserInstitutionAccessForProduct(information, productIds);
      }
      else {
        access = this.getUserGroupAccessForProduct(information, productIds);
      }

      if (event_type === 'module_request' && data.siq_licence.search(database) === -1) {
        access = SIQ.ACCESS.NO_LICENSE;
      }

      parameters.set(COUNTER_PARAMETERS.account_id, data.siq_id);
      parameters.set(COUNTER_PARAMETERS.access, access);
      parameters.set(COUNTER_PARAMETERS.site_name, data.siq_name);
    }
    // Case when we are not in a group or an institution
    else {
      parameters.set(COUNTER_PARAMETERS.access, '');
      parameters.set(COUNTER_PARAMETERS.account_id, '');
    }

    // add user individual access
    parameters.set(COUNTER_PARAMETERS.user_access, userId !== '' ? this.getUserIndividualAccessForProduct(information, productIds) : '');
    parameters.set(COUNTER_PARAMETERS.doi, doi !== null ? doi : '');
    parameters.set(COUNTER_PARAMETERS.session_id, data.session_id ? window.imaios.sha256(data.session_id).toString() : '');
    parameters.set(COUNTER_PARAMETERS.device, ScholarlyIq.getDeviceId());

    // send data for only product subscriber
    const hostImaiosEvent = imaios.get('host-imaios-event');

    ScholarlyIq.sendImaiosEvent(parameters, hostImaiosEvent);
  }

  getUserIndividualAccessForProduct(information, productIds) {
    if (productIds.some(productId => typeof information.access[productId] !== 'undefined')) {
      const access = Object.keys(information.access);

      for (const prdId of access) {
        if (
          productIds.includes(prdId)
          && information.access[prdId].individual_premium === true
        ) {
          return SIQ.ACCESS.GRANTED;
        }
      }
    }
    return SIQ.ACCESS.NO_LICENSE;
  }

  getUserInstitutionAccessForProduct(information, productIds) {
    if (productIds.some(productId => typeof information.access[productId] !== 'undefined')) {
      const access = Object.keys(information.access);

      for (const prdId of access) {
        if (
          productIds.includes(prdId)
          && (information.access[prdId].ip_premium === true)
        ) {
          return SIQ.ACCESS.GRANTED;
        }
      }
    }
    return SIQ.ACCESS.NO_LICENSE;
  }

  getUserGroupAccessForProduct(information, productIds) {
    if (productIds.some(productId => typeof information.access[productId] !== 'undefined')) {
      const access = Object.keys(information.access);

      for (const prdId of access) {
        if (
          productIds.includes(prdId)
          && (information.access[prdId].group_premium === true)
        ) {
          return SIQ.ACCESS.GRANTED;
        }
      }
    }
    return SIQ.ACCESS.NO_LICENSE;
  }

  /**
   *
   * @param {SiqInformations} data
   */
  updateDataFromThirdPartyProviderCookie(data) {
    const thirdPartyProviderCookie = getCookie(COOKIE.THIRD_PARTY_PROVIDER);
    if (thirdPartyProviderCookie) {
      try {
        /*** @type {ThirdPartyCookieData}*/
        const dataFromCookie = JSON.parse(decodeURIComponent(thirdPartyProviderCookie));
        data.siq_id = dataFromCookie.scholarlyiq_id ?? '';
        data.siq_name = dataFromCookie.provider ?? '';
        // Third party cookie is from (tokenauth)/(api/get-jwt-from-url) so it's never a B2B IP
        data.siq_b2b_is_ip = false;

        const licences = [];
        data.siq_licence = dataFromCookie.product
          .split(',')
          .map(product => (MAP_PRODUCT_SHORTCODE_DATABASE[product] ?? null))
          .filter(maybeProduct => maybeProduct !== null)
          .join(',');
      }
      catch (err) {
        console.error(err);
      }
    }
  }

  /**
   * @param {SiqInformations} data
   * @param information
   * @return {string}
   */
  getUserId(data, information) {
    const thirdPartyProviderCookie = getCookie(COOKIE.THIRD_PARTY_PROVIDER);
    if (thirdPartyProviderCookie) {
      try {
        /** @type {ThirdPartyCookieData}*/
        const dataFromCookie = JSON.parse(decodeURIComponent(thirdPartyProviderCookie));
        return `${data.siq_id}_${dataFromCookie.cuid}`;
      }
      catch (err) {
        console.error(err);
      }
    }

    return information.id ?? '';
  }
}
